/**
 * Area presence ("quem está aqui", David 2026-10-28) on the platform socket.
 *
 * Screens hold a place in a room (`area:<name>`, `space:<id>`, `item:<type>:<id>`),
 * counted per caller; the store tells the server the net result, one frame at a
 * time (the server closes a socket above 5 frames a second): `presence.join
 * { room, editing }` when a room gains its first holder or its editing flag
 * changes, `presence.leave { room }` when its last holder goes. The server
 * answers with `presence` frames built for this socket (never yourself), kept
 * here per room for `usePresence`. `presence.active` goes at most every 10 s
 * while there is input or focus. On every `ready` (first or after a drop) the
 * places are sent again; while the socket is down the frames are cleared.
 */

export interface PresencePerson {
  memberId: string
  name: string | null
  avatarUrl: string | null
  editing: boolean
  state: 'active' | 'idle'
  lastActiveAt?: string
  /** A placeholder for an editor the server does not name (community chat off): no id, name or face. */
  anonymous?: boolean
}

export interface PresenceFrame {
  room: string
  members: PresencePerson[]
  count: number
  editingCount: number
  /** Community chat off: `count`/`editingCount` are the other editors, and nobody is named (`members` is empty). */
  anonymous?: boolean
  at?: string
}

/** The server keeps at most 3 places per socket. */
export const MAX_PLACES = 3
/** Spacing between presence frames, so they never add up to the server's 5 frames/s with the room frames. */
const FRAME_GAP_MS = 300
/** Changes of one render (a screen leaving, the next one arriving) are told once. */
const RECONCILE_MS = 60
export const ACTIVITY_GAP_MS = 10_000

/** `item:<type>:<id>` with the id lowercased (the spelling every server publish uses). */
export const itemRoom = (type: string, id: string | null | undefined): string | null =>
  (id && UUID.test(id) ? `item:${type}:${id.toLowerCase()}` : null)
export const spaceRoom = (id: string | null | undefined): string | null =>
  (id && UUID.test(id) ? `space:${id.toLowerCase()}` : null)
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const asPerson = (raw: unknown): PresencePerson | null => {
  const p = raw as Partial<PresencePerson> | null
  if (!p || typeof p.memberId !== 'string') return null
  return {
    memberId: p.memberId,
    name: typeof p.name === 'string' ? p.name : null,
    avatarUrl: typeof p.avatarUrl === 'string' ? p.avatarUrl : null,
    editing: Boolean(p.editing),
    state: p.state === 'idle' ? 'idle' : 'active',
    lastActiveAt: typeof p.lastActiveAt === 'string' ? p.lastActiveAt : undefined,
  }
}

/** A `presence` frame of the socket, or null when it is not one. */
export const parsePresenceFrame = (event: Record<string, unknown>): PresenceFrame | null => {
  if (event.type !== 'presence' || typeof event.room !== 'string') return null
  const members = (Array.isArray(event.members) ? event.members : []).map(asPerson).filter((p): p is PresencePerson => Boolean(p))
  return {
    room: event.room,
    members,
    count: typeof event.count === 'number' ? event.count : members.length,
    editingCount: typeof event.editingCount === 'number' ? event.editingCount : members.filter((m) => m.editing && m.state === 'active').length,
    ...(event.anonymous === true ? { anonymous: true } : {}),
    at: typeof event.at === 'string' ? event.at : undefined,
  }
}

export const createPresenceStore = (send: (frame: Record<string, unknown>) => boolean) => {
  /** room -> holder id -> editing */
  const holders = new Map<string, Map<number, boolean>>()
  /** What the server was told since the last `ready`: room -> editing. */
  const told = new Map<string, boolean>()
  const frames = new Map<string, PresenceFrame>()
  const listeners = new Map<string, Set<() => void>>()
  const queue: Record<string, unknown>[] = []
  let ready = false
  let nextHolder = 1
  let pumpTimer: number | null = null
  let lastFrameAt = 0
  let reconcileTimer: number | null = null
  let lastActivityAt = 0

  const notify = (room: string) => {
    for (const listener of [...(listeners.get(room) || [])]) listener()
  }

  const pump = () => {
    if (pumpTimer !== null) return
    const step = () => {
      pumpTimer = null
      if (!queue.length) return
      const wait = lastFrameAt + FRAME_GAP_MS - Date.now()
      if (wait > 0) {
        pumpTimer = window.setTimeout(step, wait)
        return
      }
      const frame = queue.shift()!
      lastFrameAt = Date.now()
      send(frame)
      if (queue.length) pumpTimer = window.setTimeout(step, FRAME_GAP_MS)
    }
    step()
  }

  const enqueue = (frame: Record<string, unknown>) => {
    queue.push(frame)
    pump()
  }

  const desired = () => {
    const out = new Map<string, boolean>()
    for (const [room, byHolder] of holders) {
      if (!byHolder.size) continue
      out.set(room, [...byHolder.values()].some(Boolean))
    }
    return out
  }

  const reconcile = () => {
    reconcileTimer = null
    if (!ready) return
    const want = desired()
    for (const room of [...told.keys()]) {
      if (want.has(room)) continue
      told.delete(room)
      enqueue({ type: 'presence.leave', room })
      if (frames.delete(room)) notify(room)
    }
    for (const [room, editing] of want) {
      if (told.get(room) === editing) continue
      /* The server keeps 3 places; a fourth would be refused anyway. */
      if (!told.has(room) && told.size >= MAX_PLACES) continue
      told.set(room, editing)
      enqueue({ type: 'presence.join', room, editing })
    }
  }

  const scheduleReconcile = () => {
    if (reconcileTimer !== null) return
    reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS)
  }

  return {
    /** Holds a place in `room`; returns the release. */
    hold(room: string, editing: boolean): () => void {
      const id = nextHolder++
      if (!holders.has(room)) holders.set(room, new Map())
      holders.get(room)!.set(id, editing)
      scheduleReconcile()
      return () => {
        const byHolder = holders.get(room)
        if (!byHolder) return
        byHolder.delete(id)
        if (!byHolder.size) holders.delete(room)
        scheduleReconcile()
      }
    },
    /** `ready` arrived: the server knows nothing of this socket's places. */
    onReady() {
      ready = true
      told.clear()
      queue.length = 0
      reconcile()
    },
    /** The socket closed: no frames are true any more. */
    onDown() {
      ready = false
      told.clear()
      queue.length = 0
      for (const room of [...frames.keys()]) {
        frames.delete(room)
        notify(room)
      }
    },
    onFrame(frame: PresenceFrame) {
      if (!holders.has(frame.room)) return
      frames.set(frame.room, frame)
      notify(frame.room)
    },
    onDenied(room: string) {
      told.delete(room)
      if (frames.delete(room)) notify(room)
    },
    /** Input or focus: `presence.active`, at most every 10 s, only while some place is held. */
    activity() {
      if (!ready || !told.size) return
      const now = Date.now()
      if (now - lastActivityAt < ACTIVITY_GAP_MS) return
      lastActivityAt = now
      enqueue({ type: 'presence.active' })
    },
    get(room: string): PresenceFrame | null {
      return frames.get(room) || null
    },
    subscribe(room: string, listener: () => void): () => void {
      if (!listeners.has(room)) listeners.set(room, new Set())
      listeners.get(room)!.add(listener)
      return () => {
        const set = listeners.get(room)
        if (!set) return
        set.delete(listener)
        if (!set.size) listeners.delete(room)
      }
    },
    dispose() {
      if (pumpTimer !== null) window.clearTimeout(pumpTimer)
      if (reconcileTimer !== null) window.clearTimeout(reconcileTimer)
      pumpTimer = null
      reconcileTimer = null
    },
  }
}

export type PresenceStore = ReturnType<typeof createPresenceStore>
